/**
 * Ocean cargo section of the search form (FCL / LCL). Part of LaneSearchForm;
 * its values map to the neutral `ocean` block (oceanRequest.ts). The
 * commodity and temperature range are not asked for: reefer-ness is the
 * container type itself.
 */
import React, { useMemo } from 'react';
import { Button, Checkbox, Form } from 'antd';
import { CloseOutlined } from '@ant-design/icons';

import { CustomInput, CustomSelect } from '../../../components';
import { integerRule, numberRule } from './formRules';
import { CONTAINER_TYPES, MAX_ITEMS, OceanItemValues } from './oceanRequest';
import styles from './styles.module.css';

const UNIT_TYPE_OPTIONS = [
  { value: 'PALLET', label: 'Pallet' },
  { value: 'BOX', label: 'Box' },
];

const OceanCargoSection: React.FC<{ title?: string }> = ({
  title = 'Cargo',
}) => {
  const form = Form.useFormInstance();

  const shipmentType =
    (Form.useWatch('shipmentType', form) as 'FCL' | 'LCL' | undefined) || 'FCL';
  const containerType =
    (Form.useWatch('containerType', form) as string | undefined) || 'DRY';
  const lclMode =
    (Form.useWatch('lclMode', form) as 'PER_UNIT' | 'TOTAL' | undefined) ||
    'PER_UNIT';
  const watchedItems = Form.useWatch('items', form) as
    OceanItemValues[] | undefined;

  const sizeOptions = useMemo(
    () =>
      (
        CONTAINER_TYPES.find((t) => t.value === containerType) ??
        CONTAINER_TYPES[0]
      ).sizes,
    [containerType]
  );

  // Client-side LCL summary (totals only; rates come from the API).
  const lclSummary = useMemo(() => {
    if (shipmentType !== 'LCL' || !Array.isArray(watchedItems)) return null;
    let totalWeight = 0;
    let totalVolume = 0;
    let units = 0;
    for (const item of watchedItems) {
      const qty = Number(item?.quantity) || 0;
      const weight = Number(item?.weight) || 0;
      units += qty;
      totalWeight += qty * weight;
      if (lclMode === 'TOTAL') {
        totalVolume += Number(item?.volume) || 0;
      } else {
        const l = Number(item?.length) || 0;
        const w = Number(item?.width) || 0;
        const h = Number(item?.height) || 0;
        totalVolume += (qty * (l * w * h)) / 1_000_000; // cm³ → m³
      }
    }
    if (!units) return null;
    // Ocean LCL chargeable basis: W/M — greater of tonnes and m³.
    const chargeable = Math.max(totalWeight / 1000, totalVolume);
    return { units, totalWeight, totalVolume, chargeable };
  }, [shipmentType, lclMode, watchedItems]);

  return (
    <>
      <div className={styles.sectionTitle}>{title}</div>
      <Form.Item label='Shipment type' name='shipmentType'>
        <CustomSelect
          width='100%'
          height='36px'
          options={[
            { value: 'FCL', label: 'FCL — full container' },
            { value: 'LCL', label: 'LCL — less than container' },
          ]}
        />
      </Form.Item>

      {shipmentType === 'FCL' ? (
        <>
          <Form.Item label='Container type' name='containerType'>
            <CustomSelect
              width='100%'
              height='36px'
              options={CONTAINER_TYPES.map((t) => ({
                value: t.value,
                label: t.label,
              }))}
              onChange={() => {
                // Sizes belong to the family — clear stale row selections.
                const items = (form.getFieldValue('items') ||
                  []) as OceanItemValues[];
                form.setFieldValue(
                  'items',
                  items.map((it): OceanItemValues => ({
                    ...it,
                    containerSizeType: undefined,
                  }))
                );
              }}
            />
          </Form.Item>
          <Form.List name='items'>
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <div
                    key={field.key}
                    className={`${styles.cargoRow} ${
                      fields.length > 1
                        ? styles.cargoRowThreeWithRemove
                        : styles.cargoRowThree
                    }`}
                  >
                    <Form.Item
                      label='Container size'
                      name={[field.name, 'containerSizeType']}
                      style={{ marginBottom: 12 }}
                      rules={[{ required: true, message: 'Pick a size' }]}
                    >
                      <CustomSelect
                        placeholder='Size'
                        width='100%'
                        height='36px'
                        options={sizeOptions}
                      />
                    </Form.Item>
                    <Form.Item
                      label='Quantity'
                      name={[field.name, 'quantity']}
                      style={{ marginBottom: 12 }}
                      rules={integerRule('Whole number, min 1')}
                    >
                      <CustomInput placeholder='1' height='36px' />
                    </Form.Item>
                    <Form.Item
                      label='Weight (kg)'
                      name={[field.name, 'weight']}
                      style={{ marginBottom: 12 }}
                      rules={numberRule('Required')}
                    >
                      <CustomInput placeholder='kg' height='36px' />
                    </Form.Item>
                    {fields.length > 1 && (
                      <Button
                        type='text'
                        size='small'
                        aria-label='Remove container'
                        icon={<CloseOutlined />}
                        onClick={() => remove(field.name)}
                        style={{ marginTop: 32 }}
                      />
                    )}
                  </div>
                ))}
                {fields.length < MAX_ITEMS && (
                  <button
                    type='button'
                    className={styles.addLink}
                    onClick={() => add({ quantity: 1 })}
                  >
                    + Add container
                  </button>
                )}
              </>
            )}
          </Form.List>
        </>
      ) : (
        <>
          <Form.Item label='Dimensions entered as' name='lclMode'>
            <CustomSelect
              width='100%'
              height='36px'
              options={[
                { value: 'PER_UNIT', label: 'Per unit (L × W × H)' },
                { value: 'TOTAL', label: 'Total shipment volume (m³)' },
              ]}
            />
          </Form.Item>
          <Form.List name='items'>
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => (
                  <div key={field.key} className={styles.packageLine}>
                    {fields.length > 1 && (
                      <div className={styles.packageLineHeader}>
                        <span className={styles.muted}>
                          Cargo line {index + 1}
                        </span>
                        <Button
                          type='text'
                          size='small'
                          aria-label='Remove cargo line'
                          icon={<CloseOutlined />}
                          onClick={() => remove(field.name)}
                        />
                      </div>
                    )}
                    <div
                      className={`${styles.cargoRow} ${styles.cargoRowThree}`}
                    >
                      <Form.Item
                        label='Unit'
                        name={[field.name, 'unitType']}
                        style={{ marginBottom: 12 }}
                      >
                        <CustomSelect
                          placeholder='Pallet'
                          width='100%'
                          height='36px'
                          options={UNIT_TYPE_OPTIONS}
                        />
                      </Form.Item>
                      <Form.Item
                        label='Quantity'
                        name={[field.name, 'quantity']}
                        style={{ marginBottom: 12 }}
                        rules={integerRule('Whole number, min 1')}
                      >
                        <CustomInput placeholder='1' height='36px' />
                      </Form.Item>
                      <Form.Item
                        label='Weight per unit (kg)'
                        name={[field.name, 'weight']}
                        style={{ marginBottom: 12 }}
                        rules={numberRule('Required')}
                      >
                        <CustomInput placeholder='kg' height='36px' />
                      </Form.Item>
                    </div>
                    {lclMode === 'TOTAL' ? (
                      <Form.Item
                        label='Total volume (m³)'
                        name={[field.name, 'volume']}
                        style={{ marginBottom: 12 }}
                        rules={numberRule('Required')}
                      >
                        <CustomInput placeholder='m³' height='36px' />
                      </Form.Item>
                    ) : (
                      <div
                        className={`${styles.cargoRow} ${styles.cargoRowDims}`}
                      >
                        {(['length', 'width', 'height'] as const).map((dim) => (
                          <Form.Item
                            key={dim}
                            label={`${dim.charAt(0).toUpperCase()}${dim.slice(1)} (cm)`}
                            name={[field.name, dim]}
                            style={{ marginBottom: 12 }}
                            rules={numberRule('Required')}
                          >
                            <CustomInput placeholder='cm' height='36px' />
                          </Form.Item>
                        ))}
                      </div>
                    )}
                    <Form.Item
                      name={[field.name, 'stackable']}
                      valuePropName='checked'
                      style={{ marginBottom: 8 }}
                    >
                      <Checkbox>Stackable</Checkbox>
                    </Form.Item>
                  </div>
                ))}
                {fields.length < MAX_ITEMS && (
                  <button
                    type='button'
                    className={styles.addLink}
                    onClick={() =>
                      add({ quantity: 1, unitType: 'PALLET', stackable: true })
                    }
                  >
                    + Add cargo line
                  </button>
                )}
                {lclSummary && (
                  <div className={styles.summaryBox}>
                    <div className={styles.summaryRow}>
                      <span className={styles.summaryLabel}>Total weight</span>
                      <span>
                        {lclSummary.totalWeight.toLocaleString()} kg (
                        {lclSummary.units} unit
                        {lclSummary.units === 1 ? '' : 's'})
                      </span>
                    </div>
                    <div className={styles.summaryRow}>
                      <span className={styles.summaryLabel}>Total volume</span>
                      <span>{lclSummary.totalVolume.toFixed(2)} m³</span>
                    </div>
                    <div className={styles.summaryRow}>
                      <span className={styles.summaryLabel}>
                        Chargeable (W/M)
                      </span>
                      <span>{lclSummary.chargeable.toFixed(2)} w/m</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </Form.List>
        </>
      )}

      <div className={styles.checkboxStack}>
        <Form.Item name='hazardous' valuePropName='checked'>
          <Checkbox>Hazmat (IMDG dangerous goods)</Checkbox>
        </Form.Item>
      </div>
      <Form.Item label='Reference (optional)' name='reference'>
        <CustomInput
          maxLength={30}
          placeholder='PO # or internal reference'
          height='36px'
        />
      </Form.Item>
    </>
  );
};

export default OceanCargoSection;
